/*
  assets/littleg-lead-exit-router.js — COMMERCIAL-READINESS-GAP-CLOSURE-01
  Updated: 2026-09-03

  Owner:
  - lead_type -> next_destination -> URL routing for post-card exits.
  - Aligns with LittleGResultStorage and GCityPartnersServiceLaneMap.

  Boundary:
  - Route resolution and link attribute sync only.
  - No target page content, backend mutation, checkout or GFun ledger writes.
*/
(function (window, document) {
  'use strict';

  var VERSION = '2026-09-03-commercial-readiness-gap-closure-01c';
  if (window.LittleGLeadExitRouter && window.LittleGLeadExitRouter.version === VERSION) return;

  var RULES = {
    city_issue: { destination: 'gcitypartners', path: '/pages/gcitypartners', intent: 'city-issue-review', zh: '交給 GCityPartners', en: 'Send to GCityPartners' },
    gcitypartners: { destination: 'gcitypartners', path: '/pages/gcitypartners', intent: 'gcitypartners-entry', zh: '交給 GCityPartners', en: 'Send to GCityPartners' },
    gproperty: { destination: 'gproperty', path: '/pages/gproperty', intent: 'property-lead', zh: '交給 GProperty', en: 'Send to GProperty' },
    gtender: { destination: 'gtender', path: '/pages/gtender', intent: 'tender-lead', zh: '交給 GTender', en: 'Send to GTender' },
    partner_opportunity: { destination: 'gtender', path: '/pages/gtender', intent: 'partner-opportunity-candidate', zh: '形成項目卡', en: 'Create city opportunity card', launch_gate: 'patch18_dependency_gated' },
    corporate: { destination: 'corporate_site', path: 'https://glkgjt.com', intent: 'corporate-lead', zh: '前往正式企業入口', en: 'Open corporate entry' }
  };
  RULES.corporate_site = RULES.corporate;

  var FALLBACK = {
    destination: 'cards',
    path: '/pages/result-history',
    intent: 'unknown-lead-type',
    zh: '回到任務卡中心',
    en: 'Back to card center'
  };

  var CARD_ROOT_SELECTOR = '[data-lead-record-card], [data-finish], [data-littleg-city-mission-engine], [data-customer-result-history]';

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function key(value) {
    return clean(value).toLowerCase().replace(/-/g, '_').replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  }

  function isZh(root) {
    var locale = clean(root && root.dataset ? root.dataset.locale : '');
    if (!locale) locale = clean(document.documentElement.lang);
    return locale.toLowerCase().indexOf('zh') === 0;
  }

  function attr(node, name, fallback) {
    return clean(node && node.dataset && node.dataset[name]) || fallback || '';
  }

  function ruleFor(leadType, destination) {
    var lt = key(leadType || 'city_issue');
    var nd = key(destination || '');
    return RULES[lt] || RULES[nd] || FALLBACK;
  }

  function canonicalLane(value) {
    var raw = clean(value);
    if (!raw) return '';
    if (window.GCityPartnersServiceLaneMap && typeof window.GCityPartnersServiceLaneMap.canonical === 'function') {
      return window.GCityPartnersServiceLaneMap.canonical(raw) || raw;
    }
    return raw;
  }

  function payloadFrom(node) {
    var root = node && node.closest ? node.closest(CARD_ROOT_SELECTOR) : null;
    var rawLane = attr(node, 'serviceLane', attr(root, 'serviceLane', ''));

    return {
      lead_type: attr(node, 'leadType', attr(root, 'leadType', 'city_issue')),
      lead_category: attr(node, 'leadCategory', attr(root, 'leadCategory', '')),
      next_destination: attr(node, 'nextDestination', attr(root, 'nextDestination', '')),
      status: attr(node, 'status', attr(root, 'status', 'local_saved')),
      formal_submission_status: attr(node, 'formalSubmissionStatus', attr(root, 'formalSubmissionStatus', 'not_submitted')),
      city_pack_id: attr(node, 'cityPackId', attr(root, 'cityPackId', '')),
      city: attr(node, 'city', attr(root, 'city', '')),
      district: attr(node, 'district', attr(root, 'district', '')),
      record_id: attr(node, 'recordId', attr(root, 'recordId', '')),
      result_id: attr(node, 'resultId', attr(root, 'resultId', '')),
      daily_scene_id: attr(node, 'dailySceneId', attr(root, 'dailySceneId', '')),
      service_lane: canonicalLane(rawLane),
      opportunity_status: attr(node, 'opportunityStatus', attr(root, 'opportunityStatus', 'candidate')),
      visibility: attr(node, 'visibility', attr(root, 'visibility', 'private')),
      source: attr(node, 'source', 'littleg')
    };
  }

  function build(payload) {
    payload = payload || {};
    var storage = window.LittleGResultStorage;
    if (storage && typeof storage.buildLeadExitUrl === 'function') {
      try { return storage.buildLeadExitUrl(payload); } catch (_) {}
    }

    var r = ruleFor(payload.lead_type, payload.next_destination);
    var isExternal = /^https?:\/\//i.test(r.path);
    var url;
    try { url = new URL(r.path, window.location.origin); }
    catch (_) { url = new URL(FALLBACK.path, window.location.origin); }

    url.searchParams.set('source', payload.source || 'littleg');
    url.searchParams.set('intent', r.intent);
    url.searchParams.set('lead_type', key(payload.lead_type || 'city_issue'));
    url.searchParams.set('next_destination', r.destination);
    url.searchParams.set('status', payload.status || 'local_saved');
    url.searchParams.set('formal_submission_status', payload.formal_submission_status || 'not_submitted');

    if (payload.city_pack_id) url.searchParams.set('city_pack_id', payload.city_pack_id);
    if (payload.city) url.searchParams.set('city', payload.city);
    if (payload.district) url.searchParams.set('district', payload.district);
    if (payload.record_id) {
      url.searchParams.set('record_id', payload.record_id);
      url.searchParams.set('result_id', payload.record_id);
    }
    if (payload.lead_category) url.searchParams.set('lead_category', payload.lead_category);
    if (payload.daily_scene_id) url.searchParams.set('daily_scene_id', payload.daily_scene_id);
    if (payload.service_lane) {
      var lane = canonicalLane(payload.service_lane);
      url.searchParams.set('scene', lane);
      url.searchParams.set('service_lane', lane);
      if (r.destination === 'gcitypartners') url.searchParams.set('scenario', lane);
    }
    if (payload.opportunity_status) url.searchParams.set('opportunity_status', payload.opportunity_status);
    if (payload.visibility) url.searchParams.set('visibility', payload.visibility);
    if (r.launch_gate) url.searchParams.set('launch_gate', r.launch_gate);
    if (r.destination === 'gcitypartners') url.hash = 'gcp-brief-form';

    return isExternal ? url.href : (url.pathname + url.search + url.hash);
  }

  function labelFor(payload, zhLocale) {
    var storage = window.LittleGResultStorage;
    if (storage && typeof storage.leadExitLabel === 'function') {
      try { return storage.leadExitLabel(payload, zhLocale ? 'zh' : 'en'); } catch (_) {}
    }
    var r = ruleFor(payload && payload.lead_type, payload && payload.next_destination);
    return zhLocale ? r.zh : r.en;
  }

  function visualLabelFor(link, payload, root) {
    var explicit = attr(link, 'leadExitVisualLabel', '');
    if (explicit) return explicit;

    var type = key(payload && payload.lead_type);
    var dest = ruleFor(payload && payload.lead_type, payload && payload.next_destination).destination;
    var zh = isZh(root);

    if (type === 'partner_opportunity') return zh ? '形成項目卡' : 'Create city opportunity card';
    if (root && root.hasAttribute && root.hasAttribute('data-lead-record-card') && (type === 'city_issue' || type === 'gcitypartners' || dest === 'gcitypartners')) {
      return zh ? '需要正式處理？' : 'Need formal handling?';
    }
    return labelFor(payload, zh);
  }

  function sync(scope) {
    var target = scope || document;
    var nodes = target.querySelectorAll ? target.querySelectorAll('[data-lead-exit]') : [];

    Array.prototype.forEach.call(nodes, function (link) {
      var payload = payloadFrom(link);
      var href = build(payload);
      var root = link.closest(CARD_ROOT_SELECTOR) || document.documentElement;
      var rule = ruleFor(payload.lead_type, payload.next_destination);
      var visible = visualLabelFor(link, payload, root);

      link.href = href;
      link.dataset.nextDestination = rule.destination;
      link.dataset.leadType = key(payload.lead_type || 'city_issue');
      link.dataset.leadExitRouterVersion = VERSION;
      link.dataset.postCardExit = 'secondary';
      if (rule.launch_gate) link.dataset.patch18LaunchGate = rule.launch_gate;
      if (link.hasAttribute('data-lead-exit-label')) link.textContent = visible;

      var labelNode = root.querySelector ? root.querySelector('[data-next-destination-label]') : null;
      if (labelNode) labelNode.textContent = visible;

      if (root && root.setAttribute) {
        root.setAttribute('data-patch14-router-sync', 'true');
        root.setAttribute('data-patch18-router-sync', 'true');
        root.setAttribute('data-funnel-exit-router-contract', 'commercial-readiness-gap-closure-01');
      }
    });
  }

  function boot() {
    sync(document);
  }

  window.LittleGLeadExitRouter = Object.freeze({
    version: VERSION,
    rules: RULES,
    resolve: ruleFor,
    build: build,
    label: labelFor,
    visualLabel: visualLabelFor,
    sync: sync
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  window.addEventListener('littleg:city-mission:result-saved', boot);
})(window, document);